import { Accordion, ActionIcon, Box, Button, Checkbox, Group, Menu, ScrollArea, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconArrowsMove, IconDotsVertical, IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { EditLayerForm } from "shared/components/organization/geolocations/EditLayerForm";
import { MoveShapeForm } from "shared/components/organization/geolocations/MoveShapeForm";
import { GeoFilter, GeoLayer } from "shared/components/organization/geolocations/types";
import { archiveGeoLayer } from "shared/components/organization/geolocations/useArchiveGeoLayer";
import { useGeoLocations } from "shared/components/organization/geolocations/useGeoLocations";
import { notify } from "shared/utils/notify";

export const LayersList = () => {
    const geo = useGeoLocations();
    const [openedLayers, setOpenedLayers] = useState<string[]>([]);

    useEffect(() => {
        setOpenedLayers((current) => current.filter((id) => geo.layers.some((layer) => layer.id === id)));
    }, [geo.layers]);

    useEffect(() => {
        openedLayers.forEach((id) => {
            const layer = geo.layers.find((l) => l.id === id);
            if (layer && !layer.filters) void geo.handleFetchLayerShapes(id);
        });
    }, [openedLayers, geo.layers]);

    function handleAccordionChange(values: string[]) {
        const newlyOpened = values.find((value) => !openedLayers.includes(value));
        if (newlyOpened) void geo.handleFetchLayerShapes(newlyOpened);
        setOpenedLayers(values);
    }

    return (
        <ScrollArea style={{ height: "100%" }}>
            {geo.layers.length === 0 ? (
                <Text size="sm">No layers found.</Text>
            ) : (
                <Accordion
                    multiple
                    chevronPosition="left"
                    value={openedLayers}
                    onChange={(values) => handleAccordionChange(values)}
                    styles={{
                        control: {
                            padding: "0.5rem",
                            width: "auto",
                            backgroundColor: "transparent",
                        },
                        panel: { padding: "0.75rem" },
                        chevron: { margin: 0 },
                    }}
                >
                    {geo.layers.map((layer) => (
                        <LayerItem key={layer.id} layer={layer} />
                    ))}
                </Accordion>
            )}
        </ScrollArea>
    );
};

const LayerItem = ({ layer }: { layer: GeoLayer }) => {
    const geo = useGeoLocations();

    function handleEditLayer() {
        modals.open({
            title: "Edit Layer",
            children: <EditLayerForm layer={layer} onCancel={modals.closeAll} />,
        });
    }

    function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
        if (event.currentTarget.checked) {
            void geo.handleFetchLayerShapes(layer.id);
        }
        geo.handleToggleLayerVisibility(layer.id);
    }

    return (
        <Accordion.Item value={layer.id}>
            <Box
                style={{
                    display: "flex",
                    alignItems: "center",
                    paddingRight: "0.75rem",
                }}
            >
                <Accordion.Control aria-label={`Toggle ${layer.name}`} />
                <Box pl="xs" style={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                        checked={geo.hiddenLayerIds[layer.id] === false}
                        onChange={handleCheckboxChange}
                        aria-label={`Toggle ${layer.name}`}
                        size="xs"
                    />
                </Box>
                <Box style={{ flex: 1, paddingLeft: "0.5rem", paddingRight: "0.5rem" }}>
                    <Text fw={600}>{layer.name}</Text>
                </Box>
                <Group gap="xs" wrap="nowrap">
                    <Button
                        variant="subtle"
                        size="compact-xs"
                        leftSection={<IconPlus size={12} />}
                        onClick={() => geo.startNewShape(layer.id, true)}
                        disabled={!!geo.drawingLayerId}
                    >
                        Add Shape
                    </Button>
                    <Menu withinPortal position="bottom-end" shadow="sm">
                        <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" aria-label={`${layer.name} actions`}>
                                <IconDotsVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item leftSection={<IconEdit size={16} />} onClick={handleEditLayer}>
                                Edit
                            </Menu.Item>
                            <Menu.Item leftSection={<IconTrash size={16} />} color="red" onClick={() => archiveGeoLayer(layer)}>
                                Archive
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Box>
            <Accordion.Panel aria-label={`${layer.name} shapes`}>
                <LayerShapes layer={layer} />
            </Accordion.Panel>
        </Accordion.Item>
    );
};

const LayerShapes = ({ layer }: { layer: GeoLayer }) => {
    const geo = useGeoLocations();
    const shapes = layer.filters ?? [];

    function handleMoveShape(shape: GeoFilter) {
        const availableLayers = geo.layers.filter((l) => l.id !== layer.id);
        if (!availableLayers.length) {
            notify.show.info({ message: "No other layers available" });
            return;
        }
        modals.open({
            title: `Move ${shape.name}`,
            children: <MoveShapeForm shape={shape} availableLayers={availableLayers} onCancel={modals.closeAll} />,
        });
    }

    return (
        <Stack gap="xs">
            {shapes.length === 0 ? (
                <Text size="xs">{!layer.filters ? "Loading shapes..." : "No shapes yet"}</Text>
            ) : (
                shapes.map((shape) => (
                    <Group
                        key={shape.id}
                        justify="space-between"
                        wrap="nowrap"
                        p="xs"
                        bg={geo.hoveredShapeId === shape.id ? "var(--mantine-color-default-hover)" : undefined}
                        style={{ borderRadius: 6, transition: "background-color 150ms ease" }}
                        onMouseEnter={() => geo.setHoveredShapeId(shape.id)}
                        onMouseLeave={() => geo.setHoveredShapeId(null)}
                    >
                        <Text size="sm">{shape.name}</Text>
                        <Menu withinPortal position="bottom-end" shadow="sm">
                            <Menu.Target>
                                <ActionIcon variant="subtle" color="gray" size="sm" aria-label={`${shape.name} actions`}>
                                    <IconDotsVertical size={16} />
                                </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item leftSection={<IconEdit size={16} />} onClick={() => geo.handleEditShape(shape, layer.id)}>
                                    Edit
                                </Menu.Item>
                                <Menu.Item leftSection={<IconArrowsMove size={16} />} onClick={() => handleMoveShape(shape)}>
                                    Move
                                </Menu.Item>
                                <Menu.Item
                                    leftSection={<IconTrash size={16} />}
                                    color="red"
                                    onClick={() => void geo.handleArchiveShape(shape.id)}
                                >
                                    Archive
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </Group>
                ))
            )}
        </Stack>
    );
};
